import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getGoals, createGoal, updateGoal, deleteGoal, completeGoal, getTargets } from '../services/api';

const Goals = () => {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [formData, setFormData] = useState({
    targetId: '',
    plannedDate: '',
    reminders: [],
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [goalsRes, targetsRes] = await Promise.all([
        getGoals(),
        getTargets()
      ]);
      setGoals(goalsRes.data);
      setTargets(targetsRes.data);
    } catch (err) {
      setError('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const dataToSend = {
        ...formData,
        reminders: formData.reminders.filter(r => r) // Remove empty strings
      };

      if (editingGoal) {
        await updateGoal(editingGoal._id, dataToSend);
      } else {
        await createGoal(dataToSend);
      }
      resetForm();
      loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'Errore nel salvataggio');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (goal) => {
    setEditingGoal(goal);
    setFormData({
      targetId: goal.targetId._id,
      plannedDate: formatDateForInput(goal.plannedDate),
      reminders: goal.reminders?.map(r => formatDateTimeForInput(r.date)) || [],
      notes: goal.notes || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo obiettivo?')) return;

    try {
      await deleteGoal(id);
      loadData();
    } catch (err) {
      setError('Errore nell\'eliminazione');
    }
  };

  const handleComplete = async (id) => {
    try {
      await completeGoal(id);
      loadData();
    } catch (err) {
      setError('Errore nel completamento');
    }
  };

  const resetForm = () => {
    setFormData({ targetId: '', plannedDate: '', reminders: [], notes: '' });
    setEditingGoal(null);
    setShowForm(false);
  };

  const addReminder = () => {
    setFormData({ ...formData, reminders: [...formData.reminders, ''] });
  };

  const updateReminder = (index, value) => {
    const newReminders = [...formData.reminders];
    newReminders[index] = value;
    setFormData({ ...formData, reminders: newReminders });
  };

  const removeReminder = (index) => {
    const newReminders = formData.reminders.filter((_, i) => i !== index);
    setFormData({ ...formData, reminders: newReminders });
  };

  const formatDateForInput = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const formatDateTimeForInput = (dateString) => {
    const date = new Date(dateString);
    const offset = date.getTimezoneOffset();
    const localDate = new Date(date.getTime() - offset * 60 * 1000);
    return localDate.toISOString().slice(0, 16);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('it-IT', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getReminderStatus = (reminders) => {
    if (!reminders || reminders.length === 0) {
      return { text: 'Nessun promemoria', className: '' };
    }

    const now = new Date();
    const pending = reminders.filter(r => !r.sent);
    const sent = reminders.filter(r => r.sent);

    if (pending.length === 0 && sent.length > 0) {
      return { text: `📧 ${sent.length} promemoria inviati`, className: '' };
    }

    const nextPending = pending.sort((a, b) => new Date(a.date) - new Date(b.date))[0];
    const isPast = nextPending && new Date(nextPending.date) < now;

    if (isPast) {
      return { text: `⏰ Promemoria in attesa`, className: 'reminder-past' };
    }

    return { text: `⏰ ${pending.length} promemoria programmati`, className: '' };
  };

  if (loading) return <div className="loading">Caricamento...</div>;

  const pendingGoals = goals.filter(g => !g.completed);
  const completedGoals = goals.filter(g => g.completed);

  return (
    <div className="page-container">
      <div className="page-header-flex">
        <h1>Obiettivi</h1>
        <button onClick={() => { resetForm(); setShowForm(!showForm); }} className="btn btn-primary">
          {showForm ? 'Annulla' : 'Nuovo obiettivo'}
        </button>
      </div>

      <p className="page-description">
        Pianifica le visite che vuoi fare e ricevi promemoria via email.
      </p>

      {!user?.email && (
        <div className="warning-message">
          Per ricevere i promemoria via email, configura il tuo indirizzo email nella <Link to="/profile">pagina Profilo</Link>.
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-group">
            <label htmlFor="targetId">Persona da visitare</label>
            <select
              id="targetId"
              value={formData.targetId}
              onChange={(e) => setFormData({ ...formData, targetId: e.target.value })}
              required
            >
              <option value="">Seleziona...</option>
              {targets.map((target) => (
                <option key={target._id} value={target._id}>
                  {target.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="plannedDate">Data visita prevista</label>
            <input
              type="date"
              id="plannedDate"
              value={formData.plannedDate}
              onChange={(e) => setFormData({ ...formData, plannedDate: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label>Promemoria (opzionale)</label>
            {formData.reminders.map((reminder, index) => (
              <div key={index} className="reminder-row">
                <input
                  type="datetime-local"
                  value={reminder}
                  onChange={(e) => updateReminder(index, e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => removeReminder(index)}
                  className="btn btn-danger btn-small"
                >
                  Rimuovi
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={addReminder}
              className="btn btn-secondary btn-small"
              style={{ marginTop: '0.5rem' }}
            >
              + Aggiungi promemoria
            </button>
          </div>

          <div className="form-group">
            <label htmlFor="notes">Note (opzionale)</label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows="3"
              placeholder="Appunti sulla visita..."
            />
          </div>

          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Salvataggio...' : (editingGoal ? 'Aggiorna' : 'Crea obiettivo')}
          </button>
        </form>
      )}

      {pendingGoals.length === 0 && completedGoals.length === 0 ? (
        <p className="empty-state">Nessun obiettivo. Creane uno per ricevere promemoria!</p>
      ) : (
        <>
          {pendingGoals.length > 0 && (
            <div className="goals-section">
              <h2>Da completare ({pendingGoals.length})</h2>
              <div className="goal-list">
                {pendingGoals.map((goal) => {
                  const reminderStatus = getReminderStatus(goal.reminders);
                  return (
                    <div key={goal._id} className="goal-card">
                      <div className="goal-header">
                        <h3>{goal.targetId?.name}</h3>
                        <span className={`goal-status ${reminderStatus.className}`}>
                          {reminderStatus.text}
                        </span>
                      </div>
                      <div className="goal-details">
                        <p><strong>📅 Visita:</strong> {formatDate(goal.plannedDate)}</p>
                        {goal.reminders && goal.reminders.length > 0 && (
                          <div className="goal-reminders">
                            <strong>🔔 Promemoria:</strong>
                            <ul>
                              {goal.reminders.map((r, idx) => (
                                <li key={idx} className={r.sent ? 'reminder-sent' : ''}>
                                  {formatDateTime(r.date)}
                                  {r.sent && ' ✓'}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {goal.notes && <p><strong>📝 Note:</strong> {goal.notes}</p>}
                      </div>
                      <div className="goal-actions">
                        <button onClick={() => handleComplete(goal._id)} className="btn btn-success btn-small">
                          Completato
                        </button>
                        <button onClick={() => handleEdit(goal)} className="btn btn-secondary btn-small">
                          Modifica
                        </button>
                        <button onClick={() => handleDelete(goal._id)} className="btn btn-danger btn-small">
                          Elimina
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {completedGoals.length > 0 && (
            <div className="goals-section completed-section">
              <h2>Completati ({completedGoals.length})</h2>
              <div className="goal-list">
                {completedGoals.map((goal) => (
                  <div key={goal._id} className="goal-card goal-completed">
                    <div className="goal-header">
                      <h3>{goal.targetId?.name}</h3>
                      <span className="goal-status completed">✓ Completato</span>
                    </div>
                    <div className="goal-details">
                      <p><strong>📅 Visita:</strong> {formatDate(goal.plannedDate)}</p>
                    </div>
                    <div className="goal-actions">
                      <button onClick={() => handleDelete(goal._id)} className="btn btn-danger btn-small">
                        Elimina
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Goals;
